import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Card, Textarea, Select, ArrayInput, ImageUploader } from '../components';
import { addListing, updateListing as updateListingApi, getListingById } from '../api';
import type { UserListingData, ListingDetails, Availability, SelectOption, KeysOfNonArrayObjectProperties } from '../types';
import { PlaceType } from '../types';
import { Timestamp, serverTimestamp, storage, storageRef, uploadBytes, getDownloadURL } from '../firebase';
import { MAX_PROFILE_IMAGES, MAX_ROOM_IMAGES } from '../constants';
import LottieSpinner from '../components/LottieSpinner';
import { useTranslation } from 'react-i18next';
import GooglePlacesAutocompleteInput from '../components/GooglePlacesAutocompleteInput';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

const initialListingDetails: ListingDetails = {
  amenities: [],
  availability: {
    from: Timestamp.fromDate(new Date()),
    to: Timestamp.fromDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1))),
    minRentalPeriod: "Onbepaald",
    notes: ""
  },
  houseRules: [],
  location: { address: "", coordinates: { latitude: 0, longitude: 0 } },
  placeType: PlaceType.Room,
  preferences: {
    ageRange: { min: 18, max: 99 },
    gender: "Niet belangrijk",
    occupation: []
  },
  price: 0,
  roomImages: [],
  roommates: {
    count: 0,
    description: "",
    types: [],
    ageRange: { min: 18, max: 99 }
  },
  squareMeters: 0,
};

export const AddOrEditListingPage: React.FC = () => {
  const { t, i18n } = useTranslation();

  const { listingId } = useParams<{ listingId?: string }>();
  const isEditMode = Boolean(listingId);
  const { currentUser, agentData, setGlobalNotification } = useAuth();
  const navigate = useNavigate();

  const [listingDescription, setListingDescription] = useState('');
  const [details, setDetails] = useState<ListingDetails>(initialListingDetails);
  const [profileImageFiles, setProfileImageFiles] = useState<File[]>([]);
  const [roomImageFiles, setRoomImageFiles] = useState<File[]>([]);
  const [profileImages, setProfileImages] = useState<string[]>([]);
  const [roomImages, setRoomImages] = useState<string[]>(details.roomImages || []);
  
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);
  const [formError, setFormError] = useState<string | null>(null);
  const [customAmenity, setCustomAmenity] = useState('');
  const [customHouseRule, setCustomHouseRule] = useState('');

  const AMENITIES_OPTIONS = t('amenities_options', { returnObjects: true }) as string[];
  const HOUSE_RULES_OPTIONS = t('house_rules_options', { returnObjects: true }) as string[];

  // PlaceType mapping for translation
  const placeTypeValueToLabel = {
    Kamer: t('place_type_room', 'Kamer'),
    Studio: t('place_type_studio', 'Studio'),
    Appartement: t('place_type_apartment', 'Appartement'),
    Huis: t('place_type_house', 'Huis'),
  };
  const placeTypeOptions: SelectOption[] = Object.entries(placeTypeValueToLabel).map(([value, label]) => ({ value, label }));

  const genderValueToLabel = {
    'Niet belangrijk': t('gender_pref_not_important', 'Not important'),
    'Man': t('gender_pref_male', 'Man'),
    'Vrouw': t('gender_pref_female', 'Vrouw'),
  };
  const genderPreferenceOptions: SelectOption[] = Object.entries(genderValueToLabel).map(([value, label]) => ({ value, label }));

  const minRentalPeriodValueToLabel = {
    'Onbepaald': t('min_rental_period_indefinite', 'Indefinite'),
    'Korter dan 6 maanden': t('min_rental_period_less_than_6_months', 'Less than 6 months'),
    '6 maanden': t('min_rental_period_6_months', '6 months'),
    '12 maanden': t('min_rental_period_12_months', '12 months'),
    'Langer dan 12 maanden': t('min_rental_period_more_than_12_months', 'More than 12 months'),
  };
  const minRentalPeriodOptions: SelectOption[] = Object.entries(minRentalPeriodValueToLabel).map(([value, label]) => ({ value, label }));

  useEffect(() => {
    if (isEditMode && listingId) {
      setPageLoading(true);
      getListingById(listingId)
        .then(listingDoc => {
          if (listingDoc) {
            if(listingDoc.agentUid !== currentUser?.uid) {
                setGlobalNotification({ message: "U heeft geen toestemming om deze woning te bewerken.", type: 'error'});
                navigate('/dashboard');
                return;
            }
            setListingDescription(listingDoc.description);
            // Ensure date fields are correctly formatted for input[type=date] if they exist
            const fetchedDetails = listingDoc.listing;
            if (fetchedDetails.availability.from && fetchedDetails.availability.from.toDate) {
                 fetchedDetails.availability.from = Timestamp.fromDate(fetchedDetails.availability.from.toDate());
            }
            if (fetchedDetails.availability.to && fetchedDetails.availability.to.toDate) {
                 fetchedDetails.availability.to = Timestamp.fromDate(fetchedDetails.availability.to.toDate());
            }
            setDetails(fetchedDetails);
            setProfileImages(listingDoc.profileImages || []);
            setRoomImages(listingDoc.listing.roomImages || []);
          } else {
            setGlobalNotification({ message: "Woning niet gevonden.", type: 'error'});
            navigate('/dashboard');
          }
        })
        .catch(err => {
          console.error("Error fetching listing for edit:", err);
          setGlobalNotification({ message: "Fout bij laden woning.", type: 'error'});
          navigate('/dashboard');
        })
        .finally(() => setPageLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, listingId, currentUser?.uid, navigate, setGlobalNotification]);


  const handleDetailChange = <K extends keyof ListingDetails>(
    field: K,
    value: ListingDetails[K]
  ) => {
    setDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = <
    P extends KeysOfNonArrayObjectProperties<ListingDetails>, // Ensure P is a key for a non-array object property
    K extends keyof ListingDetails[P]
  >(
    parentField: P,
    childField: K,
    value: ListingDetails[P][K]
  ) => {
    setDetails(prev => ({
      ...prev,
      [parentField]: {
        ...(prev[parentField] as object), // Cast to object to satisfy spread
        [childField]: value,
      },
    }));
  };
  
  const handleAvailabilityChange = (field: keyof Availability, value: string) => {
    if (field === 'from' || field === 'to') {
        const dateValue = value ? Timestamp.fromDate(new Date(value)) : Timestamp.fromDate(new Date());
         handleNestedChange('availability', field, dateValue);
    } else {
        handleNestedChange('availability', field, value as Availability[typeof field]);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentUser || !agentData) {
      setFormError("Uw account is niet goedgekeurd voor het toevoegen van woningen.");
      return;
    }
    setLoading(true);
    setFormError(null);

    const listingDataPayload: UserListingData = {
      agentUid: currentUser.uid,
      userType: "LISTING",
      email: agentData.email,
      firstName: agentData.contactPerson,
      lastName: agentData.companyName || "", 
      description: listingDescription,
      listing: details,
      profileImages: [], // Eerst leeg, vullen na upload
      onboardingCompleted: true,
      createdAt: isEditMode && listingId ? (details.availability.from) : Timestamp.now(),
      updatedAt: serverTimestamp() as Timestamp,
    };

    try {
      let newListingId = listingId;
      if (isEditMode && listingId) {
        // Bij bewerken: upload nieuwe images, voeg downloadURLs toe aan bestaande arrays
        // (Voor nu: alleen toevoegen, geen verwijderen van oude images)
        // ... (kan later uitgebreid worden)
      } else {
        // 1. Listing aanmaken (krijg id)
        newListingId = await addListing(listingDataPayload);
      }

      // 2. Upload images naar storage
      // Storage-pad is eigendom van de agent (auth.uid), niet van het listing-doc:
      //   agent-listings/{agentUid}/{listingId}/{folder}/{file}
      // De oude paden (profile-images/{listingId}) werden door de Storage-rules geweigerd
      // omdat listingId ≠ auth.uid — daardoor kwamen portal-listings zonder foto's online.
      const agentUid = currentUser?.uid;
      if (!agentUid) throw new Error('Niet ingelogd');
      // Foto's gaan tegelijk omhoog. Eerder wachtte elke foto op de vorige, inclusief
      // de wachttijd op de resize-extensie — bij vijf foto's tikte dat op tot ~40s.
      const uploadImagesAndGetUrls = async (files: File[], folder: string) => {
        const batchStamp = Date.now();
        return Promise.all(files.map(async (file, idx) => {
          const uniqueName = `${batchStamp}-${idx}-${Math.floor(Math.random() * 10000)}`;
          const origExt = file.name.split('.').pop() || 'jpg';
          const basePath = `agent-listings/${agentUid}/${newListingId}/${folder}`;
          const imgRef = storageRef(storage, `${basePath}/${uniqueName}.${origExt}`);
          await uploadBytes(imgRef, file);

          // Wacht kort op de geschaalde versie (_1080x1080) van de resize-extensie;
          // val anders terug op het origineel.
          const resizedRef = storageRef(storage, `${basePath}/${uniqueName}_1080x1080.jpeg`);
          for (let attempt = 0; attempt < 16; attempt++) { // max ~8s (500ms per poging)
            try {
              return await getDownloadURL(resizedRef);
            } catch {
              await new Promise(res => setTimeout(res, 500));
            }
          }
          return getDownloadURL(imgRef);
        }));
      };

      const [profileImageUrls, roomImageUrls] = await Promise.all([
        uploadImagesAndGetUrls(profileImageFiles, 'profile-images'),
        uploadImagesAndGetUrls(roomImageFiles, 'room-images'),
      ]);

      // 3. Update listing met image URLs
      const allProfileImages = [...profileImages, ...profileImageUrls];
      const allRoomImages = [...roomImages, ...roomImageUrls];
      await updateListingApi(newListingId!, {
        profileImages: allProfileImages,
        listing: {
          ...details,
          roomImages: allRoomImages,
        },
      });

      setGlobalNotification({ message: isEditMode ? 'Woning succesvol bijgewerkt!' : 'Woning succesvol toegevoegd!', type: 'success' });
      navigate('/dashboard');
    } catch (err: any) {
      console.error("Error saving listing:", err);
      setFormError(err.message || 'Fout bij opslaan van de woning.');
      setGlobalNotification({ message: err.message || 'Fout bij opslaan.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  function handleAddCustomAmenity() {
    if (customAmenity.trim() && !details.amenities.includes(customAmenity.trim())) {
      handleDetailChange('amenities', [...details.amenities, customAmenity.trim()]);
      setCustomAmenity('');
    }
  }

  function handleAddCustomHouseRule() {
    if (customHouseRule.trim() && !details.houseRules.includes(customHouseRule.trim())) {
      handleDetailChange('houseRules', [...details.houseRules, customHouseRule.trim()]);
      setCustomHouseRule('');
    }
  }

  if (pageLoading) return <div className="flex justify-center items-center h-[calc(100vh-4rem)]"><LottieSpinner size={100} /></div>;

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold text-neutral-800 mb-8 mt-10">
        {isEditMode ? t('edit_listing', 'Woning Bewerken') : t('add_listing')}
      </h1>
      <Card>
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {formError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">{formError}</p>}

          {/* Info tekst voor beschrijving */}
          <div className="mb-3">
            <div className="flex items-start gap-2 bg-neutral-50 border border-neutral-200 rounded p-3">
              <InformationCircleIcon className="h-5 w-5 text-[#EA4160] mt-0.5" />
              <span className="text-sm text-neutral-700">
                {t('listing_description_info')}
                {/* nl: Geef een korte, duidelijke beschrijving van de woning. Wat maakt deze woning uniek of aantrekkelijk voor huurders? */}
              </span>
            </div>
          </div>
          <Textarea label={t('description') || 'Algemene beschrijving woning'} value={listingDescription} onChange={e => setListingDescription(e.target.value)} required rows={3} />
          
          <ImageUploader
            images={profileImageFiles}
            setImages={setProfileImageFiles}
            existingImages={profileImages}
            setExistingImages={setProfileImages}
            maxImages={MAX_PROFILE_IMAGES}
            label={t('profile_images')}
            hint={t('profile_images_hint')}
          />

          {/* Info tekst voor kamerfoto's */}
          <div className="mb-3">
            <div className="flex items-start gap-2 bg-neutral-50 border border-neutral-200 rounded p-3">
              <InformationCircleIcon className="h-5 w-5 text-[#EA4160] mt-0.5" />
              <span className="text-sm text-neutral-700">
                {t('listing_photos_info')}
                {/* nl: Voeg duidelijke foto's toe van de kamer en gemeenschappelijke ruimtes. Goede foto's verhogen de kans op een match! */}
              </span>
            </div>
          </div>
          <ImageUploader
            images={roomImageFiles}
            setImages={setRoomImageFiles}
            existingImages={roomImages}
            setExistingImages={setRoomImages}
            maxImages={MAX_ROOM_IMAGES}
            label={t('room_images')}
          />

          <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6 border p-4 rounded-md">
            <legend className="text-lg font-semibold px-2">{t('basic_info') || 'Basisgegevens'}</legend>
            <GooglePlacesAutocompleteInput
              label={t('address') || 'Adres'}
              value={details.location.address ? details.location : null}
              onChange={val => handleDetailChange('location', val ? val : { address: '', coordinates: { latitude: 0, longitude: 0 } })}
            />
            <Input label={t('price_per_month') || 'Prijs per maand (€)'} type="number" value={isNaN(details.price) ? '' : details.price} onChange={e => handleDetailChange('price', parseFloat(e.target.value) || 0)} required min="0" />
            <Select label={t('place_type') || 'Type woning'} options={placeTypeOptions} value={details.placeType} onChange={value => handleDetailChange('placeType', value as PlaceType)} required />
            <Input label={t('square_meters') || 'Oppervlakte (m²)'} type="number" value={isNaN(details.squareMeters) ? '' : details.squareMeters} onChange={e => handleDetailChange('squareMeters', parseInt(e.target.value) || 0)} required min="0" />
          </fieldset>
          
          <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6 border p-4 rounded-md">
            <legend className="text-lg font-semibold px-2">{t('availability') || 'Beschikbaarheid'}</legend>
            <div className="col-span-2 mb-3">
              <div className="flex items-start gap-2 bg-neutral-50 border border-neutral-200 rounded p-3 mb-2">
                <InformationCircleIcon className="h-5 w-5 text-[#EA4160] mt-0.5" />
                <span className="text-sm text-neutral-700">
                  {t('listing_availability_info')}
                  {/* nl: Geef aan vanaf wanneer de kamer beschikbaar is en voor hoe lang. Zo weten huurders wanneer ze kunnen intrekken. */}
                </span>
              </div>
            </div>
            <Input label={t('available_from') || 'Beschikbaar vanaf'} type="date" 
                   value={details.availability.from instanceof Timestamp ? details.availability.from.toDate().toISOString().split('T')[0] : ''} 
                   onChange={e => handleAvailabilityChange('from', e.target.value)} required />
            <Input label={t('available_to') || 'Beschikbaar tot'} type="date" 
                   value={details.availability.to instanceof Timestamp ? details.availability.to.toDate().toISOString().split('T')[0] : ''} 
                   onChange={e => handleAvailabilityChange('to', e.target.value)} required />
            <Select label={t('min_rental_period') || 'Minimale huurperiode'} options={minRentalPeriodOptions} value={details.availability.minRentalPeriod} onChange={value => handleAvailabilityChange('minRentalPeriod', value)} />
            <Textarea label={t('availability_notes') || 'Notities beschikbaarheid'} value={details.availability.notes || ""} onChange={e => handleAvailabilityChange('notes', e.target.value)} rows={2}/>
          </fieldset>

          {/* Voorzieningen (amenities) */}
          <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6 border p-4 rounded-md">
            <legend className="text-lg font-semibold px-2">{t('amenities')}</legend>
            <div className="col-span-2 mb-3">
              <div className="flex items-start gap-2 bg-neutral-50 border border-neutral-200 rounded p-3 mb-2">
                <InformationCircleIcon className="h-5 w-5 text-[#EA4160] mt-0.5" />
                <span className="text-sm text-neutral-700">
                  {t('listing_amenities_info')}
                  {/* nl: Selecteer welke voorzieningen beschikbaar zijn in de woning. Dit helpt potentiële huurders om te bepalen of de woning bij hen past. */}
                </span>
              </div>
            </div>
            <div className="col-span-2">
              <div className="flex flex-wrap gap-2 mb-2">
                {AMENITIES_OPTIONS.map(option => (
                  <button
                    type="button"
                    key={option}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all flex items-center gap-1 ${details.amenities.includes(option) ? 'bg-[#EA4160] text-white border-[#EA4160]' : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-100'}`}
                    onClick={() => {
                      if (details.amenities.includes(option)) {
                        handleDetailChange('amenities', details.amenities.filter(a => a !== option));
                      } else {
                        handleDetailChange('amenities', [...details.amenities, option]);
                      }
                    }}
                  >
                    {option}
                  </button>
                ))}
                {/* Custom amenities as buttons */}
                {details.amenities.filter(a => !AMENITIES_OPTIONS.includes(a)).map(option => (
                  <button
                    type="button"
                    key={option}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all bg-[#EA4160] text-white border-[#EA4160]`}
                    onClick={() => handleDetailChange('amenities', details.amenities.filter(a => a !== option))}
                    title="Klik om te verwijderen"
                  >
                    {option}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EA4160]/50"
                  placeholder="Eigen voorziening toevoegen..."
                  value={customAmenity}
                  onChange={e => setCustomAmenity(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomAmenity(); }}}
                />
                <Button type="button" onClick={handleAddCustomAmenity} disabled={!customAmenity.trim()} size="small">
                  Toevoegen
                </Button>
              </div>
            </div>
          </fieldset>

          {/* Huisregels (houseRules) */}
          <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6 border p-4 rounded-md">
            <legend className="text-lg font-semibold px-2">{t('house_rules')}</legend>
            <div className="col-span-2 mb-3">
              <div className="flex items-start gap-2 bg-neutral-50 border border-neutral-200 rounded p-3 mb-2">
                <InformationCircleIcon className="h-5 w-5 text-[#EA4160] mt-0.5" />
                <span className="text-sm text-neutral-700">
                  {t('listing_houserules_info')}
                  {/* nl: Geef aan welke regels gelden in de woning. Zo weten potentiële huurders wat er van hen verwacht wordt. */}
                </span>
              </div>
            </div>
            <div className="col-span-2">
              <div className="flex flex-wrap gap-2 mb-2">
                {HOUSE_RULES_OPTIONS.map(option => (
                  <button
                    type="button"
                    key={option}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all flex items-center gap-1 ${details.houseRules.includes(option) ? 'bg-[#EA4160] text-white border-[#EA4160]' : 'bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-100'}`}
                    onClick={() => {
                      if (details.houseRules.includes(option)) {
                        handleDetailChange('houseRules', details.houseRules.filter(a => a !== option));
                      } else {
                        handleDetailChange('houseRules', [...details.houseRules, option]);
                      }
                    }}
                  >
                    {option}
                  </button>
                ))}
                {/* Custom house rules as buttons */}
                {details.houseRules.filter(a => !HOUSE_RULES_OPTIONS.includes(a)).map(option => (
                  <button
                    type="button"
                    key={option}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all bg-[#EA4160] text-white border-[#EA4160]`}
                    onClick={() => handleDetailChange('houseRules', details.houseRules.filter(a => a !== option))}
                    title="Klik om te verwijderen"
                  >
                    {option}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input
                  type="text"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EA4160]/50"
                  placeholder="Eigen huisregel toevoegen..."
                  value={customHouseRule}
                  onChange={e => setCustomHouseRule(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCustomHouseRule(); }}}
                />
                <Button type="button" onClick={handleAddCustomHouseRule} disabled={!customHouseRule.trim()} size="small">
                  Toevoegen
                </Button>
              </div>
            </div>
          </fieldset>

          <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6 border p-4 rounded-md">
            <legend className="text-lg font-semibold px-2">{t('tenant_preferences') || 'Huurdersvoorkeuren'}</legend>
            <div className="col-span-2 mb-3">
              <div className="flex items-start gap-2 bg-neutral-50 border border-neutral-200 rounded p-3 mb-2">
                <InformationCircleIcon className="h-5 w-5 text-[#EA4160] mt-0.5" />
                <span className="text-sm text-neutral-700">
                  {t('tenant_preferences_info')}
                </span>
              </div>
            </div>
            <Input label={t('min_age_pref') || 'Min. leeftijd voorkeur'} type="number" value={isNaN(details.preferences.ageRange.min) ? '' : details.preferences.ageRange.min} onChange={e => handleNestedChange('preferences', 'ageRange', { ...details.preferences.ageRange, min: parseInt(e.target.value) || 0})} min="16" max="99" />
            <Input label={t('max_age_pref') || 'Max. leeftijd voorkeur'} type="number" value={isNaN(details.preferences.ageRange.max) ? '' : details.preferences.ageRange.max} onChange={e => handleNestedChange('preferences', 'ageRange', { ...details.preferences.ageRange, max: parseInt(e.target.value) || 0})} min="16" max="99" />
            <Select label={t('gender_pref') || 'Geslachtsvoorkeur'} options={genderPreferenceOptions} value={details.preferences.gender} onChange={value => handleNestedChange('preferences', 'gender', value)} />
            <ArrayInput id="occupationPrefs" label={t('occupation_prefs') || 'Beroepsvoorkeur(en)'} items={details.preferences.occupation} setItems={items => handleNestedChange('preferences', 'occupation', items)} placeholder="Bijv. Student"/>
          </fieldset>

          {/* <fieldset className="grid grid-cols-1 md:grid-cols-2 gap-6 border p-4 rounded-md">
            <legend className="text-lg font-semibold px-2">{t('roommates') || 'Huisgenoten (indien van toepassing)'}</legend>
            <Input label={t('roommate_count') || 'Aantal huidige huisgenoten'} type="number" value={details.roommates.count} onChange={e => handleNestedChange('roommates', 'count', parseInt(e.target.value))} min="0" />
            <Input label={t('roommate_min_age') || 'Min. leeftijd huisgenoten'} type="number" value={details.roommates.ageRange.min} onChange={e => handleNestedChange('roommates', 'ageRange', { ...details.roommates.ageRange, min: parseInt(e.target.value)})} min="16" max="99" />
            <Input label={t('roommate_max_age') || 'Max. leeftijd huisgenoten'} type="number" value={details.roommates.ageRange.max} onChange={e => handleNestedChange('roommates', 'ageRange', { ...details.roommates.ageRange, max: parseInt(e.target.value)})} min="16" max="99" />
            <Textarea label={t('roommate_description') || 'Beschrijving huisgenoten'} value={details.roommates.description} onChange={e => handleNestedChange('roommates', 'description', e.target.value)} rows={2} />
            <ArrayInput id="roommateTypes" label={t('roommate_types') || 'Type huisgenoten'} items={details.roommates.types} setItems={items => handleNestedChange('roommates', 'types', items)} placeholder="Bijv. Studenten"/>
          </fieldset> */}

          <div className="flex flex-col md:flex-row gap-4 mt-6">
            <Button
              type="submit"
              className="w-full md:w-auto py-3 px-8 text-base font-semibold rounded-lg"
              isLoading={loading}
              disabled={loading}
            >
              {isEditMode ? t('save') : t('add')}
            </Button>
            {isEditMode && (
              <Button
                type="button"
                className="w-full md:w-auto py-3 px-8 text-base font-semibold rounded-lg bg-neutral-300 text-neutral-800 hover:bg-neutral-400"
                onClick={() => navigate('/dashboard')}
                disabled={loading}
              >
                {t('cancel', 'Annuleren')}
              </Button>
            )}
          </div>
          {loading && (
            <div className="text-center text-sm text-neutral-500 mt-2">
              Afbeeldingen worden geüpload en geschaald. Dit kan tot 2 minuten duren, afhankelijk van de bestandsgrootte.
            </div>
          )}
        </form>
      </Card>
    </div>
  );
};